/**
 * Rule Editor Modal
 * Modal for creating and editing organization rules
 */

import React, { useState, useEffect } from 'react';
import {
  RuleCondition,
  CreateRuleRequest,
  OrganizationRule,
  RULE_FIELD_OPTIONS,
  RULE_OPERATOR_OPTIONS,
  FIELD_PREDEFINED_OPTIONS,
  RuleOperator,
} from '../../types/organization';
import { useCreateRule, useUpdateRule, usePreviewRule } from '../../hooks/useOrganization';
import './RuleEditorModal.css';

interface RuleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  listId: string;
  existingRule?: OrganizationRule | null;
}

export const RuleEditorModal: React.FC<RuleEditorModalProps> = ({
  isOpen,
  onClose,
  listId,
  existingRule,
}) => {
  const [ruleName, setRuleName] = useState('');
  const [conditions, setConditions] = useState<RuleCondition[]>([
    { field: '', operator: 'equals' as RuleOperator, value: '' },
  ]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const previewRuleMutation = usePreviewRule();

  // Initialize form with existing rule data
  useEffect(() => {
    if (existingRule) {
      setRuleName(existingRule.rule_name);
      setConditions(existingRule.conditions.length > 0 ? existingRule.conditions : [
        { field: '', operator: 'equals' as RuleOperator, value: '' },
      ]);
    } else {
      setRuleName('');
      setConditions([{ field: '', operator: 'equals' as RuleOperator, value: '' }]);
    }
    setPreviewCount(null);
  }, [existingRule, isOpen]);

  const handleAddCondition = () => {
    setConditions([...conditions, { field: '', operator: 'equals' as RuleOperator, value: '' }]);
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index));
    }
  };

  const handleConditionChange = (index: number, field: keyof RuleCondition, value: any) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    
    // Reset value if operator changed to is_null or is_not_null
    if (field === 'operator' && (value === 'is_null' || value === 'is_not_null')) {
      newConditions[index].value = undefined;
    }
    
    // Initialize value as empty array if operator is one_of
    if (field === 'operator' && value === 'one_of') {
      newConditions[index].value = [];
    }
    
    setConditions(newConditions);
    setPreviewCount(null); // Reset preview when conditions change
  };

  // Get predefined options for a field, or return null if none
  const getPredefinedOptions = (fieldValue: string): string[] | null => {
    return FIELD_PREDEFINED_OPTIONS[fieldValue] || null;
  };

  // Check if we should show multi-select dropdown
  const shouldShowMultiSelect = (condition: RuleCondition): boolean => {
    return condition.operator === 'one_of' && getPredefinedOptions(condition.field) !== null;
  };

  const getCompatibleOperators = (fieldValue: string) => {
    const field = RULE_FIELD_OPTIONS.find(f => f.value === fieldValue);
    if (!field) return RULE_OPERATOR_OPTIONS;
    
    return RULE_OPERATOR_OPTIONS.filter(op => 
      op.compatibleTypes.includes(field.type)
    );
  };

  const handlePreview = async () => {
    const validConditions = conditions.filter(c => c.field && c.operator);
    if (validConditions.length === 0) {
      alert('Please add at least one valid condition');
      return;
    }

    try {
      const result = await previewRuleMutation.mutateAsync({
        conditions: validConditions,
        limit: 100,
      });
      setPreviewCount(result.matching_ads_count);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Failed to preview rule');
    }
  };

  const handleSave = () => {
    if (!ruleName.trim()) {
      alert('Please enter a rule name');
      return;
    }

    const validConditions = conditions.filter(c => c.field && c.operator);
    if (validConditions.length === 0) {
      alert('Please add at least one valid condition');
      return;
    }

    const request: CreateRuleRequest = {
      rule_name: ruleName.trim(),
      conditions: validConditions,
    };

    if (existingRule) {
      updateRule.mutate(
        {
          listId,
          ruleId: existingRule.rule_id,
          request,
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            alert(`Failed to update rule: ${errorMessage}`);
          },
        }
      );
    } else {
      createRule.mutate(
        { listId, request },
        {
          onSuccess: () => {
            onClose();
          },
          onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            alert(`Failed to create rule: ${errorMessage}`);
          },
        }
      );
    }
  };

  if (!isOpen) return null;

  const isLoading = createRule.isPending || updateRule.isPending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="rule-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{existingRule ? 'Edit Rule' : 'Create Rule'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Rule Name</label>
            <input
              type="text"
              placeholder="e.g., UAC Android Videos"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label>Conditions (all must match - AND logic)</label>
            <div className="conditions-list">
              {conditions.map((condition, index) => (
                <div key={index} className="condition-row">
                  <select
                    className="field-select"
                    value={condition.field}
                    onChange={(e) => handleConditionChange(index, 'field', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Select field...</option>
                    {RULE_FIELD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>

                  <select
                    className="operator-select"
                    value={condition.operator}
                    onChange={(e) => handleConditionChange(index, 'operator', e.target.value as RuleOperator)}
                    disabled={isLoading || !condition.field}
                  >
                    {getCompatibleOperators(condition.field).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>

                  {/* Multi-select for "one_of" with predefined options */}
                  {shouldShowMultiSelect(condition) && (
                    <div className="multiselect-wrapper">
                      <select
                        multiple
                        className="value-multiselect"
                        value={Array.isArray(condition.value) ? condition.value : []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          handleConditionChange(index, 'value', selected);
                        }}
                        disabled={isLoading}
                        size={Math.min(5, getPredefinedOptions(condition.field)?.length || 5)}
                      >
                        {getPredefinedOptions(condition.field)?.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <span className="multiselect-hint">
                        Hold Ctrl (Cmd on Mac) to select multiple
                      </span>
                    </div>
                  )}

                  {/* Regular text input for other operators */}
                  {condition.operator !== 'is_null' && 
                   condition.operator !== 'is_not_null' && 
                   !shouldShowMultiSelect(condition) && (
                    <input
                      type="text"
                      className="value-input"
                      placeholder="Value"
                      value={condition.value || ''}
                      onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                      disabled={isLoading}
                    />
                  )}

                  <button
                    className="remove-condition-btn"
                    onClick={() => handleRemoveCondition(index)}
                    disabled={isLoading || conditions.length === 1}
                    title="Remove condition"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button
              className="add-condition-btn"
              onClick={handleAddCondition}
              disabled={isLoading}
            >
              + Add Condition
            </button>
          </div>

          <div className="preview-section">
            <button
              className="preview-btn"
              onClick={handlePreview}
              disabled={previewRuleMutation.isPending || isLoading}
            >
              {previewRuleMutation.isPending ? 'Previewing...' : 'Preview Matching Ads'}
            </button>
            {previewCount !== null && (
              <div className="preview-result">
                <strong>{previewCount}</strong> {previewCount === 1 ? 'ad matches' : 'ads match'} these conditions
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={isLoading || !ruleName.trim()}
          >
            {isLoading ? 'Saving...' : existingRule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};

